import { useMemo, useState } from "react";
import { UploadCloud, CheckCircle2, FileSpreadsheet, MapPin, Zap } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "../api";

const ACTIONS = [
  { v: "enrich", label: "Mevcut projeyi güncelle" },
  { v: "create", label: "Yeni proje oluştur" },
  { v: "skip", label: "Atla" },
];

export default function ProjectMasterImport({ onDone }) {
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null); // {filename, items, existing_projects}

  const counts = useMemo(() => {
    if (!data) return { enrich: 0, create: 0, skip: 0 };
    return data.items.reduce((a, it) => { a[it.action] = (a[it.action] || 0) + 1; return a; }, { enrich: 0, create: 0, skip: 0 });
  }, [data]);

  const doPreview = async () => {
    if (!file) return toast.error("Önce künye Excel'ini seçin");
    setLoading(true);
    const fd = new FormData(); fd.append("file", file);
    try {
      const r = await api.post("/imports/project-master/preview", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setData(r.data);
      toast.success(`${r.data.count} proje satırı okundu`);
    } catch (e) { toast.error(errMsg(e, "Analiz başarısız")); }
    finally { setLoading(false); }
  };

  const setItem = (row, patch) => setData((d) => ({ ...d, items: d.items.map((it) => it.row === row ? { ...it, ...patch } : it) }));

  const doCommit = async () => {
    if (!data) return;
    setLoading(true);
    try {
      const r = await api.post("/imports/project-master/commit", { items: data.items, filename: data.filename });
      toast.success(`Tamam · ${r.data.stats.enriched} güncellendi, ${r.data.stats.created} yeni, ${r.data.stats.skipped} atlandı`);
      setData(null); setFile(null); onDone?.();
    } catch (e) { toast.error(errMsg(e, "İçe aktarma başarısız")); }
    finally { setLoading(false); }
  };

  return (
    <div data-testid="project-master-import">
      <section className="panel upload-panel">
        <div className="upload-drop">
          <UploadCloud size={30} />
          <b>{file ? file.name : "Proje künye Excel'ini seçin (.xlsx)"}</b>
          <input type="file" accept=".xlsx" onChange={(e) => setFile(e.target.files[0])} data-testid="pm-file-input" />
        </div>
        <div className="upload-actions">
          <span className="rate-note">Firma, süreç, tür, il/ilçe, lisans ve kapasite bilgileri okunur; mevcut projelerle otomatik eşleştirilir.</span>
          <button className="new-btn" disabled={!file || loading} onClick={doPreview} data-testid="pm-preview-button">
            <FileSpreadsheet size={16} /> {loading ? "Analiz ediliyor..." : "Analiz et"}
          </button>
        </div>
      </section>

      {data && (
        <section className="panel" data-testid="pm-preview">
          <div className="panel-head">
            <div><span className="eyebrow">ÖNİZLEME · {data.filename}</span><h2>Eşleştirme & Karar</h2></div>
            <button className="new-btn" disabled={loading} onClick={doCommit} data-testid="pm-commit-button">
              <CheckCircle2 size={16} /> İçe Aktar
            </button>
          </div>
          <div className="import-stats" style={{ marginBottom: 8 }}>
            <div><small>GÜNCELLENECEK</small><b>{counts.enrich}</b></div>
            <div><small>YENİ PROJE</small><b>{counts.create}</b></div>
            <div><small>ATLANACAK</small><b>{counts.skip}</b></div>
            <div><small>TOPLAM SATIR</small><b>{data.items.length}</b></div>
          </div>

          <div className="pm-list">
            {data.items.map((it) => (
              <div className={`pm-row action-${it.action}`} key={it.row} data-testid={`pm-row-${it.row}`}>
                <div className="pm-info">
                  <b>{it.name}</b>
                  <div className="pm-tags">
                    {it.firma && <span className="pm-tag">{it.firma}</span>}
                    {it.project_type && <span className="pm-tag alt"><Zap size={10} /> {it.project_type}</span>}
                    {(it.city || it.district) && <span className="pm-tag"><MapPin size={10} /> {[it.city, it.district].filter(Boolean).join(" / ")}</span>}
                    {it.capacity_production && <span className="pm-tag">{it.capacity_production}</span>}
                    {it.process_status && <span className="pm-tag">{it.process_status}</span>}
                  </div>
                  {it.suggested && (
                    <small className="pm-match">Öneri: <b>{it.suggested.name}</b> <em className={it.suggested.score >= 0.72 ? "score-good" : "score-weak"}>%{Math.round(it.suggested.score * 100)}</em></small>
                  )}
                </div>
                <div className="pm-decide">
                  <select value={it.action} onChange={(e) => setItem(it.row, { action: e.target.value })} data-testid={`pm-action-${it.row}`}>
                    {ACTIONS.map((a) => <option key={a.v} value={a.v}>{a.label}</option>)}
                  </select>
                  {it.action === "enrich" && (
                    <select value={it.project_id || ""} onChange={(e) => setItem(it.row, { project_id: e.target.value })} data-testid={`pm-target-${it.row}`}>
                      <option value="">— Hedef proje seçin —</option>
                      {data.existing_projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                    </select>
                  )}
                </div>
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
